import { useEffect, useRef, useState } from "react";
import { researchOrganizationWebsite } from "../lib/api";
import type { WebsiteResearchState } from "../lib/types";
import { createWebsiteFallback } from "../lib/website";

/**
 * Debounced organization-website research.
 *
 * Behaviour preserved verbatim from App.tsx, including three subtleties:
 *  - the 650ms debounce, and the AbortController that cancels an in-flight lookup;
 *  - the cache is only consulted when `lookupNonce` is 0, so "Research again" always
 *    re-queries (the `!lookupNonce` quirk) — a nonce of 0 means "never manually refreshed";
 *  - a failed lookup degrades to conservative locally-generated guesses rather than an
 *    error, and labels them as such, because a wrong website silently poisons the run.
 */
export function useWebsiteResearch(organization: string) {
  const [research, setResearch] = useState<WebsiteResearchState>({
    status: "idle",
    query: "",
    suggestions: []
  });
  const [lookupNonce, setLookupNonce] = useState(0);
  const websiteCache = useRef(new Map<string, WebsiteResearchState>());

  useEffect(() => {
    const query = organization.trim();
    if (query.length < 3) {
      setResearch({ status: "idle", query, suggestions: [] });
      return;
    }

    const cacheKey = query.toLowerCase();
    const cached = websiteCache.current.get(cacheKey);
    if (cached && !lookupNonce) {
      setResearch(cached);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setResearch((current) => ({
        status: "loading",
        query,
        suggestions: current.query === query ? current.suggestions : [],
        provider: current.provider
      }));

      try {
        const result = await researchOrganizationWebsite(query, controller.signal);
        if (controller.signal.aborted) return;
        const next: WebsiteResearchState = {
          status: result.suggestions.length ? "ready" : "empty",
          query,
          suggestions: result.suggestions,
          provider: result.provider,
          message: result.message
        };
        websiteCache.current.set(cacheKey, next);
        setResearch(next);
      } catch {
        if (controller.signal.aborted) return;
        const fallback = createWebsiteFallback(query);
        const next: WebsiteResearchState = {
          status: "ready",
          query,
          suggestions: fallback,
          provider: "Local fallback",
          message: "Gemini lookup was unavailable, so these are conservative website guesses."
        };
        setResearch(next);
      }
    }, 650);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [organization, lookupNonce]);

  return {
    research,
    refresh: () => setLookupNonce((current) => current + 1)
  };
}
